import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

import { getFirebaseAuth, attemptWebSdkSyncWithFreshToken } from './auth-store';

export type AuthClaims = {
  businessId?: string;
  role?: string;
  [key: string]: unknown;
};

export type AuthSession = {
  uid: string;
  email: string | null;
  claims: AuthClaims;
  source: 'native' | 'web';
};

/**
 * Returns the current auth session using a cached (non-refreshed) token.
 * Safe to call frequently. Returns null if not authenticated.
 */
export async function getCurrentAuthSession(): Promise<AuthSession | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      const nativeResult = await FirebaseAuthentication.getCurrentUser();
      if (!nativeResult.user) return null;

      const tokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
      // Native plugin doesn't expose claims directly — read them from the web SDK
      const auth = getFirebaseAuth();
      const webUser = auth?.currentUser;
      let claims: AuthClaims = {};
      if (webUser) {
        const webToken = await webUser.getIdTokenResult(false).catch(() => null);
        claims = (webToken?.claims as AuthClaims) ?? {};
      }

      return {
        uid: nativeResult.user.uid,
        email: nativeResult.user.email ?? null,
        claims,
        source: 'native',
      };
    } catch {
      return null;
    }
  }

  const auth = getFirebaseAuth();
  const user = auth?.currentUser;
  if (!user) return null;

  try {
    const tokenResult = await user.getIdTokenResult(false);
    return {
      uid: user.uid,
      email: user.email,
      claims: tokenResult.claims as AuthClaims,
      source: 'web',
    };
  } catch {
    return null;
  }
}

/**
 * Returns a fresh auth session by force-refreshing the token from Firebase servers.
 * Guarantees the latest custom claims are included. Slightly slower due to network call.
 */
export async function getFreshAuthSession(): Promise<AuthSession | null> {
  if (Capacitor.isNativePlatform()) {
    try {
      const nativeResult = await FirebaseAuthentication.getCurrentUser();
      if (!nativeResult.user) return null;

      const auth = getFirebaseAuth();
      const webUser = auth?.currentUser;
      let claims: AuthClaims = {};
      if (webUser) {
        const webToken = await webUser.getIdTokenResult(true).catch(() => null);
        claims = (webToken?.claims as AuthClaims) ?? {};
      }

      return {
        uid: nativeResult.user.uid,
        email: nativeResult.user.email ?? null,
        claims,
        source: 'native',
      };
    } catch {
      return null;
    }
  }

  const auth = getFirebaseAuth();
  const user = auth?.currentUser;
  if (!user) return null;

  try {
    const tokenResult = await user.getIdTokenResult(true);
    return {
      uid: user.uid,
      email: user.email,
      claims: tokenResult.claims as AuthClaims,
      source: 'web',
    };
  } catch {
    return null;
  }
}

const STANDARD_JWT_CLAIMS = new Set([
  'iss', 'aud', 'auth_time', 'user_id', 'sub', 'iat', 'exp',
  'email', 'email_verified', 'firebase', 'uid',
]);

/**
 * Validates that the web SDK user has the required custom claims.
 *
 * Strategy:
 * 1. Check the cached token WITHOUT force-refreshing (preserves claims embedded
 *    in the custom token payload, which are lost on force-refresh if the backend
 *    does not call setCustomUserClaims).
 * 2. If claims are missing, attempt a web SDK re-sync via attemptWebSdkSyncWithFreshToken
 *    so the next sign-in can embed the claims again via a fresh custom token.
 * 3. After re-sync, re-check the cached token (no force-refresh).
 *
 * Root cause note: Claims are only truly persistent across token refreshes when the
 * backend calls admin.auth().setCustomUserClaims(uid, { businessId, role }).
 * Until then, claims exist only in the ~1 hour window after signInWithCustomToken.
 */
export async function requireFirebaseClaims(
  requiredClaims: string[]
): Promise<{ uid: string; claims: AuthClaims } | null> {
  const auth = getFirebaseAuth();
  const user = auth?.currentUser;

  if (!user) {
    console.warn('[Auth] requireFirebaseClaims: No web SDK user — web SDK sync may have failed');
    return null;
  }

  const checkClaims = async (forceRefresh: boolean): Promise<{ uid: string; claims: AuthClaims } | null> => {
    try {
      const tokenResult = await user.getIdTokenResult(forceRefresh);
      const claims = tokenResult.claims as AuthClaims;

      const missingClaims = requiredClaims.filter(
        (claim) => claims[claim] === undefined || claims[claim] === null
      );

      if (missingClaims.length > 0) {
        const available = Object.keys(claims).filter((k) => !STANDARD_JWT_CLAIMS.has(k));
        console.warn('[Auth] requireFirebaseClaims: Missing required claims', {
          missing: missingClaims,
          available,
          issuedAtTime: tokenResult.issuedAtTime,
          forceRefresh,
        });
        return null;
      }

      return { uid: user.uid, claims };
    } catch (error) {
      console.error('[Auth] requireFirebaseClaims error:', error);
      return null;
    }
  };

  // Step 1: Check the cached token (no force-refresh). Custom-token embedded claims
  // are present here for ~1 hour after signInWithCustomToken.
  const cached = await checkClaims(false);
  if (cached) return cached;

  // Step 2: Claims missing from cached token (likely because Firebase auto-refreshed
  // the token via the refresh-token path and the backend has not set custom claims via
  // setCustomUserClaims). Attempt a web SDK re-sync using a fresh custom token so the
  // embedded claims are restored for another hour.
  console.warn('[Auth] requireFirebaseClaims: Attempting web SDK re-sync to restore claims...');
  try {
    const resynced = await attemptWebSdkSyncWithFreshToken();
    if (resynced) {
      // After re-sync with a fresh custom token, get the cached (newly issued) token.
      // DO NOT force-refresh — that would strip the just-embedded claims.
      const afterResync = await checkClaims(false);
      if (afterResync) return afterResync;
    }
  } catch {
    // ignore — fall through to null
  }

  console.warn('[Auth] requireFirebaseClaims: Claims still missing after re-sync attempt. Backend must call admin.auth().setCustomUserClaims(uid, { businessId }) to persist claims across token refreshes.');
  return null;
}
